import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Plus, Pencil, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { DateField, Empty, money, PageTitle, today } from './shared';

const lastMonth = () => {
  const d = new Date();
  d.setDate(0);
  return d.toISOString().slice(0, 7);
};

function AssetDialog({ open, onClose, asset, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(asset ? { ...asset } : { name: '', category: 'Vehicles & Equipment', acquisitionDate: today(), cost: '', salvageValue: '0', usefulLifeYears: '5' });
  }, [open, asset]);
  const locked = Boolean(asset?.postedMonths?.length);
  const save = async () => {
    try { await ledgerApi.saveAsset(f); toast.success('Asset saved'); onSaved(); onClose(); } catch (e) { toast.error(apiError(e, 'Could not save')); }
  };
  const field = (k, label, type = 'text') => (
    <div><Label className="text-xs text-slate-500">{label}</Label>
      <Input type={type} step={type === 'number' ? '0.01' : undefined} value={f[k] ?? ''} disabled={locked && ['cost', 'usefulLifeYears'].includes(k)} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-9" /></div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{asset ? 'Edit asset' : 'New fixed asset'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{field('name', 'Name (e.g. 2024 Ford Transit)')}</div>
          {field('category', 'Category')}
          {locked ? field('acquisitionDate', 'Purchase date') : <DateField label="Purchase date" value={f.acquisitionDate || ''} onChange={(v) => setF({ ...f, acquisitionDate: v })} />}
          {field('cost', 'Cost', 'number')}
          {field('salvageValue', 'Salvage value', 'number')}
          {field('usefulLifeYears', 'Useful life (years)', 'number')}
        </div>
        <p className="text-xs text-slate-500">Straight-line, starting the month after purchase. Record the purchase itself as a bill, expense or journal entry to the Vehicles &amp; Equipment account.</p>
        {locked ? <p className="text-xs text-amber-700">Depreciation is already posted, so cost, life and date are fixed.</p> : null}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-[#8BC53F] hover:bg-[#6FA82C]" onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FixedAssets() {
  const [assets, setAssets] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [through, setThrough] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { ledgerApi.assets().then((d) => setAssets(d.assets)).catch((e) => toast.error(apiError(e, 'Could not load assets'))); }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await ledgerApi.runDepreciation(through);
      toast.success(r.posted ? `Posted ${r.posted} month${r.posted === 1 ? '' : 's'} of depreciation (${money(r.total)})` : 'Nothing new to post — already up to date');
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const totals = (assets || []).reduce((a, x) => ({ cost: a.cost + x.cost, acc: a.acc + x.accumulatedDepreciation }), { cost: 0, acc: 0 });

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-assets">
      <PageTitle title="Fixed Assets" subtitle="Trucks, rigs and equipment, depreciated month by month into the books.">
        <div><Label className="text-xs text-slate-500">Depreciate through</Label><Input type="month" value={through} onChange={(e) => setThrough(e.target.value)} className="mt-1 h-9 w-40" /></div>
        <Button variant="outline" className="h-9 gap-1.5" onClick={run} disabled={busy || !assets?.length}><PlayCircle className="w-4 h-4" />{busy ? 'Posting…' : 'Run depreciation'}</Button>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5 h-9"><Plus className="w-4 h-4" />New asset</Button>
      </PageTitle>
      <div className="grid sm:grid-cols-3 gap-3">
        {[['Cost', totals.cost], ['Accumulated depreciation', totals.acc], ['Book value', totals.cost - totals.acc]].map(([k, v]) => (
          <Card key={k} className="p-4 border-slate-200"><div className="text-xs text-slate-500">{k}</div><div className="text-2xl font-extrabold tabular-nums mt-1">{money(v)}</div></Card>
        ))}
      </div>
      <Card className="border-slate-200">
        {!assets ? <Empty>Loading…</Empty> : !assets.length ? <Empty>No fixed assets yet.</Empty> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="px-4 py-3 text-left">Asset</th><th className="px-4 py-3 text-left">Purchased</th><th className="px-4 py-3 text-right">Cost</th><th className="px-4 py-3 text-right">Life</th><th className="px-4 py-3 text-right">Monthly</th><th className="px-4 py-3 text-right">Accumulated</th><th className="px-4 py-3 text-right">Book value</th><th className="px-4 py-3 text-left">Through</th><th /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 tabular-nums">
              {assets.map((a) => {
                const monthly = (a.cost - a.salvageValue) / (a.usefulLifeYears * 12);
                const done = a.accumulatedDepreciation >= a.cost - a.salvageValue - 0.004;
                return (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5"><div className="font-semibold text-slate-900">{a.name}</div><div className="text-xs text-slate-500">{a.category}</div></td>
                    <td className="px-4 py-2.5 text-slate-600">{a.acquisitionDate}</td>
                    <td className="px-4 py-2.5 text-right">{money(a.cost)}</td>
                    <td className="px-4 py-2.5 text-right">{a.usefulLifeYears} yr</td>
                    <td className="px-4 py-2.5 text-right">{money(monthly)}</td>
                    <td className="px-4 py-2.5 text-right">{money(a.accumulatedDepreciation)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{money(a.bookValue)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{done ? <Badge className="border-0 bg-slate-100 text-slate-600">Fully depreciated</Badge> : (a.lastDepreciationDate || '—')}</td>
                    <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => { setEditing(a); setOpen(true); }}><Pencil className="w-3.5 h-3.5" />Edit</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      <AssetDialog open={open} onClose={() => setOpen(false)} asset={editing} onSaved={load} />
    </div>
  );
}
